import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {AdapterMoment, Box, ButtonBase, DateCalendar, LocalizationProvider, Popover, Stack, Typography} from 'views/components';
import {CalendarTodayOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import CohortSection from './sections/cohort/CohortSection';
import FunnelBreakdownSection from './sections/funnel_breakdown/FunnelBreakdownSection';
import KeyMetricsSection from './sections/key_metrics/KeyMetricsSection';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const LeadFunnel = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;

  const timezone = 'America/Toronto';
  const latestAvailableDate = React.useMemo(() => {
    return moment.tz(timezone).subtract(1, 'day').startOf('day');
  }, [timezone]);

  const [comparisonMode, setComparisonMode] = React.useState('WEEKLY');
  const [selectedCurrentDate, setSelectedCurrentDate] = React.useState(latestAvailableDate.format('YYYY-MM-DD'));
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [stageMetricsState, setStageMetricsState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    metricsByDate: {},
    fetchedDates: {},
    currentDate: '',
    stageComparisonDates: [],
  });
  const [logMetricsState, setLogMetricsState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    metricsByDate: {},
    fetchedDates: {},
    keyMetricsCurrentPeriodDates: [],
    keyMetricsPreviousPeriodDates: [],
    keyMetricsGraphDates: [],
  });
  const stageList = React.useMemo(() => {
    const stalledDotColor = translate('client.Status.STALLED.MetricsColor.DotColor');
    const stalledLineColor = translate('client.Status.STALLED.MetricsColor.LineColor');
    const closedLostDotColor = translate('client.Status.CLOSED_LOST.MetricsColor.DotColor');
    const closedLostLineColor = translate('client.Status.CLOSED_LOST.MetricsColor.LineColor');

    return [
      {
        key: 'LEAD',
        label: translate('client.Status.LEAD.Title'),
        dotColor: translate('client.Status.LEAD.MetricsColor.DotColor'),
        lineColor: translate('client.Status.LEAD.MetricsColor.LineColor'),
      },
      {
        key: 'CONTACT_MADE',
        label: translate('client.Status.CONTACT_MADE.Title'),
        dotColor: translate('client.Status.CONTACT_MADE.MetricsColor.DotColor'),
        lineColor: translate('client.Status.CONTACT_MADE.MetricsColor.LineColor'),
      },
      {
        key: 'DETAILS_NEEDED',
        label: translate('client.Status.DETAILS_NEEDED.Title'),
        dotColor: translate('client.Status.DETAILS_NEEDED.MetricsColor.DotColor'),
        lineColor: translate('client.Status.DETAILS_NEEDED.MetricsColor.LineColor'),
      },
      {
        key: 'CG_SEARCH',
        label: translate('client.Status.CG_SEARCH.Title'),
        dotColor: translate('client.Status.CG_SEARCH.MetricsColor.DotColor'),
        lineColor: translate('client.Status.CG_SEARCH.MetricsColor.LineColor'),
      },
      {
        key: 'CG_SEARCH_ACTIVE',
        label: translate('client.Status.CG_SEARCH_ACTIVE.Title'),
        dotColor: translate('client.Status.CG_SEARCH_ACTIVE.MetricsColor.DotColor'),
        lineColor: translate('client.Status.CG_SEARCH_ACTIVE.MetricsColor.LineColor'),
      },
      {
        key: 'CG_PROFILE_SENT',
        label: translate('client.Status.CG_PROFILE_SENT.Title'),
        dotColor: translate('client.Status.CG_PROFILE_SENT.MetricsColor.DotColor'),
        lineColor: translate('client.Status.CG_PROFILE_SENT.MetricsColor.LineColor'),
      },
      {
        key: 'MEET_GREET',
        label: translate('client.Status.MEET_GREET.Title'),
        dotColor: translate('client.Status.MEET_GREET.MetricsColor.DotColor'),
        lineColor: translate('client.Status.MEET_GREET.MetricsColor.LineColor'),
      },
      {
        key: 'ACTIVE',
        label: translate('client.Status.ACTIVE.Title'),
        dotColor: translate('client.Status.ACTIVE.MetricsColor.DotColor'),
        lineColor: translate('client.Status.ACTIVE.MetricsColor.LineColor'),
      },
      {
        key: 'STALLED',
        label: translate('client.Status.STALLED.Title'),
        dotColor: stalledDotColor,
        lineColor: stalledLineColor,
        rowBackgroundColor: `${stalledDotColor}26`,
      },
      {
        key: 'CLOSED_LOST',
        label: translate('client.Status.CLOSED_LOST.Title'),
        dotColor: closedLostDotColor,
        lineColor: closedLostLineColor,
        rowBackgroundColor: `${closedLostDotColor}26`,
      },
    ];
  }, []);
  const comparisonWindow = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone).startOf('day');
    const currentDate = currentDateMoment.format('YYYY-MM-DD');
    const previousStageDate = comparisonMode === 'DAILY' ?
      currentDateMoment.clone().subtract(1, 'day').format('YYYY-MM-DD') :
      currentDateMoment.clone().subtract(7, 'day').format('YYYY-MM-DD');

    if (comparisonMode === 'DAILY') {
      const keyMetricsGraphDates = Array.from({length: 7}, (_, index) => {
        return currentDateMoment.clone().subtract(6 - index, 'day').format('YYYY-MM-DD');
      });

      return {
        currentDate,
        stageComparisonDates: [previousStageDate],
        keyMetricsCurrentPeriodDates: [currentDate],
        keyMetricsPreviousPeriodDates: [previousStageDate],
        keyMetricsGraphDates,
      };
    }

    const keyMetricsCurrentPeriodDates = Array.from({length: 7}, (_, index) => {
      return currentDateMoment.clone().subtract(6 - index, 'day').format('YYYY-MM-DD');
    });
    const keyMetricsPreviousPeriodDates = Array.from({length: 7}, (_, index) => {
      return currentDateMoment.clone().subtract(13 - index, 'day').format('YYYY-MM-DD');
    });

    return {
      currentDate,
      stageComparisonDates: [previousStageDate],
      keyMetricsCurrentPeriodDates,
      keyMetricsPreviousPeriodDates,
      keyMetricsGraphDates: keyMetricsCurrentPeriodDates,
    };
  }, [comparisonMode, selectedCurrentDate, timezone]);

  const cohortDates = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone).startOf('day');

    return Array.from({length: 7}, (_, index) => {
      return currentDateMoment.clone().subtract(6 - index, 'day').format('YYYY-MM-DD');
    });
  }, [selectedCurrentDate, timezone]);

  const logMetricConfigs = React.useMemo(() => ([
    {
      key: 'LEADS',
      filterFn: (clientLog) => clientLog.oldStatus == null,
    },
    {
      key: 'MEET_GREET',
      filterFn: (clientLog) => clientLog.newStatus === 'MEET_GREET',
    },
    {
      key: 'STARTS',
      filterFn: (clientLog) => clientLog.newStatus === 'ACTIVE',
    },
  ]), []);
  const setsByKeyAndDate = React.useMemo(() => {
    const clientRnSetsByKeyAndDate = logMetricConfigs.reduce((nextMetricSets, logMetricConfig) => {
      nextMetricSets[logMetricConfig.key] = {};

      return nextMetricSets;
    }, {});

    Object.keys(logMetricsState.metricsByDate).forEach((date) => {
      const clientRnSetsByKey = logMetricConfigs.reduce((nextMetricSets, logMetricConfig) => {
        nextMetricSets[logMetricConfig.key] = new Set();

        return nextMetricSets;
      }, {});

      (logMetricsState.metricsByDate[date]?.clientLogs || []).forEach((clientLog) => {
        logMetricConfigs.forEach((logMetricConfig) => {
          if (logMetricConfig.filterFn(clientLog)) {
            clientRnSetsByKey[logMetricConfig.key].add(clientLog.clientRn);
          }
        });
      });

      logMetricConfigs.forEach((logMetricConfig) => {
        clientRnSetsByKeyAndDate[logMetricConfig.key][date] = clientRnSetsByKey[logMetricConfig.key];
      });
    });

    return clientRnSetsByKeyAndDate;
  }, [logMetricConfigs, logMetricsState.metricsByDate]);

  const newCustomerMetricsByDate = React.useMemo(() => {
    const startClientRnSetsByDate = setsByKeyAndDate.STARTS || {};

    return Object.keys(startClientRnSetsByDate).reduce((nextMetricsByDate, date) => {
      const clientRnSet = startClientRnSetsByDate[date] || new Set();

      nextMetricsByDate[date] = {
        statusCount: {
          NEW_CUSTOMERS: Array.from(clientRnSet),
        },
      };

      return nextMetricsByDate;
    }, {});
  }, [setsByKeyAndDate]);

  const cohortGraphSections = React.useMemo(() => ([
    {
      titleExpression: 'metrics.LeadFunnel.Cohort.TopOfFunnel',
      stageKeys: [
        'LEAD',
        'DETAILS_NEEDED',
        'CG_SEARCH',
      ],
      isLoading: stageMetricsState.isLoading && !stageMetricsState.hasLoadedOnce,
      errorMessage: stageMetricsState.errorMessage,
      metricsByDate: stageMetricsState.metricsByDate,
    },
    {
      titleExpression: 'metrics.LeadFunnel.Cohort.BottomOfFunnel',
      stageKeys: [
        'CG_SEARCH_ACTIVE',
        'CG_PROFILE_SENT',
        'MEET_GREET',
      ],
      isLoading: stageMetricsState.isLoading && !stageMetricsState.hasLoadedOnce,
      errorMessage: stageMetricsState.errorMessage,
      metricsByDate: stageMetricsState.metricsByDate,
    },
    {
      titleExpression: 'metrics.LeadFunnel.Cohort.NewCustomers',
      stageKeys: [
        'NEW_CUSTOMERS',
      ],
      isLoading: logMetricsState.isLoading && !logMetricsState.hasLoadedOnce,
      errorMessage: logMetricsState.errorMessage,
      metricsByDate: newCustomerMetricsByDate,
    },
  ]), [
    logMetricsState.errorMessage, logMetricsState.hasLoadedOnce, logMetricsState.isLoading, newCustomerMetricsByDate,
    stageMetricsState.errorMessage, stageMetricsState.hasLoadedOnce, stageMetricsState.isLoading, stageMetricsState.metricsByDate,
  ]);

  const cohortCurrentDate = cohortDates[cohortDates.length - 1] || '';
  const cohortPreviousDates = cohortDates.slice(0, -1);

  // Build the unique date sets needed for stage and log metrics to be requested.
  const requestedStageMetricDates = React.useMemo(() => {
    return Array.from(new Set([...cohortDates, comparisonWindow.currentDate, ...comparisonWindow.stageComparisonDates]));
  }, [cohortDates, comparisonWindow.currentDate, comparisonWindow.stageComparisonDates]);
  const requestedLogMetricDates = React.useMemo(() => {
    return Array.from(
        new Set([
          ...comparisonWindow.keyMetricsCurrentPeriodDates,
          ...comparisonWindow.keyMetricsPreviousPeriodDates,
          ...comparisonWindow.keyMetricsGraphDates,
        ]),
    );
  }, [comparisonWindow.keyMetricsCurrentPeriodDates, comparisonWindow.keyMetricsPreviousPeriodDates, comparisonWindow.keyMetricsGraphDates]);

  React.useEffect(() => {
    let isActive = true;

    const loadMetrics = async () => {
      const {
        currentDate,
        stageComparisonDates,
        keyMetricsCurrentPeriodDates,
        keyMetricsPreviousPeriodDates,
        keyMetricsGraphDates,
      } = comparisonWindow;
      const stageMetricDatesToFetch = requestedStageMetricDates.filter((date) => !stageMetricsState.fetchedDates[date]);
      const logMetricDatesToFetch = requestedLogMetricDates.filter((date) => !logMetricsState.fetchedDates[date]);

      setStageMetricsState((currentState) => ({
        ...currentState,
        isLoading: stageMetricDatesToFetch.length > 0,
        errorMessage: '',
      }));
      setLogMetricsState((currentState) => ({
        ...currentState,
        isLoading: logMetricDatesToFetch.length > 0,
        errorMessage: '',
      }));

      if (!stageMetricDatesToFetch.length) {
        setStageMetricsState((currentState) => ({
          ...currentState,
          currentDate,
          stageComparisonDates,
        }));
      }

      if (!logMetricDatesToFetch.length) {
        setLogMetricsState((currentState) => ({
          ...currentState,
          keyMetricsCurrentPeriodDates,
          keyMetricsPreviousPeriodDates,
          keyMetricsGraphDates,
        }));
      }

      // Optimization: do not need to fetch for dates we already fetched
      if (!stageMetricDatesToFetch.length && !logMetricDatesToFetch.length) {
        return;
      }

      await progressWrap(async () => {
        const [stageMetricsResult, logMetricsResult] = await Promise.allSettled([
          stageMetricDatesToFetch.length ? backend.metrics.queryDailyClientMetrics(stageMetricDatesToFetch) : Promise.resolve(null),
          logMetricDatesToFetch.length ? backend.metrics.queryDailyClientLogsMetrics(logMetricDatesToFetch) : Promise.resolve(null),
        ]);
        const syncFetchedMetrics = (setState, datesToFetch, result, fallbackErrorMessage, extraState) => {
          if (!datesToFetch.length) {
            return;
          }

          if (result.status === 'fulfilled') {
            setState((currentState) => {
              const metricsByDate = {
                ...currentState.metricsByDate,
              };

              result.value.metrics.forEach((metric) => {
                metricsByDate[metric.metricDate] = metric;
              });

              return {
                ...currentState,
                isLoading: false,
                hasLoadedOnce: true,
                errorMessage: '',
                metricsByDate,
                fetchedDates: {
                  ...currentState.fetchedDates,
                  ...datesToFetch.reduce((nextFetchedDates, date) => {
                    nextFetchedDates[date] = true;

                    return nextFetchedDates;
                  }, {}),
                },
                ...extraState,
              };
            });

            return;
          }

          setState((currentState) => ({
            ...currentState,
            isLoading: false,
            errorMessage: result.reason?.message || fallbackErrorMessage,
          }));
        };

        if (!isActive) {
          return;
        }

        syncFetchedMetrics(
            setStageMetricsState,
            stageMetricDatesToFetch,
            stageMetricsResult,
            translate('metrics.LeadFunnel.ClientStageMetrics.LoadError'),
            {
              currentDate,
              stageComparisonDates,
            },
        );
        syncFetchedMetrics(
            setLogMetricsState,
            logMetricDatesToFetch,
            logMetricsResult,
            translate('metrics.LeadFunnel.KeyMetrics.LoadError'),
            {
              keyMetricsCurrentPeriodDates,
              keyMetricsPreviousPeriodDates,
              keyMetricsGraphDates,
            },
        );
      });
    };

    loadMetrics();

    return () => {
      isActive = false;
    };
  }, [comparisonWindow, progressWrap, requestedLogMetricDates, requestedStageMetricDates]);

  const formattedDateRange = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone);

    if (comparisonMode === 'DAILY') {
      return currentDateMoment.format('MMM D, YYYY');
    }

    const startDate = currentDateMoment.clone().subtract(6, 'day');
    const endDate = currentDateMoment;

    return `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
  }, [comparisonMode, selectedCurrentDate, timezone]);

  const stageMissingDates = React.useMemo(() => {
    const missingDates = [];

    if (
      stageMetricsState.currentDate &&
      !stageMetricsState.metricsByDate[stageMetricsState.currentDate]
    ) {
      missingDates.push(moment.tz(stageMetricsState.currentDate, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY'));
    }

    stageMetricsState.stageComparisonDates.forEach((date) => {
      if (!stageMetricsState.metricsByDate[date]) {
        missingDates.push(moment.tz(date, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY'));
      }
    });

    return missingDates;
  }, [stageMetricsState.currentDate, stageMetricsState.metricsByDate, stageMetricsState.stageComparisonDates]);

  const stageRows = React.useMemo(() => {
    const currentMetric = stageMetricsState.metricsByDate[stageMetricsState.currentDate] || null;
    let previousMetric = null;

    if (stageMetricsState.stageComparisonDates.length) {
      previousMetric = stageMetricsState.metricsByDate[stageMetricsState.stageComparisonDates[0]] || null;
    }
    const currentStatusCount = currentMetric?.statusCount || {};
    const previousStatusCount = previousMetric?.statusCount || {};

    return stageList.map((stage) => {
      const nowCount = currentMetric ? currentStatusCount[stage.key]?.length || 0 : null;
      const prevCount = previousMetric ? previousStatusCount[stage.key]?.length || 0 : null;
      let delta = null;

      if (nowCount !== null && prevCount !== null) {
        delta = nowCount - prevCount;
      }

      return {
        ...stage,
        nowCount,
        prevCount,
        delta,
      };
    });
  }, [stageList, stageMetricsState.currentDate, stageMetricsState.metricsByDate, stageMetricsState.stageComparisonDates]);

  const stageDistributionMax = React.useMemo(() => {
    const includedStageRows = stageRows.filter((stage) => {
      return stage.key !== 'CLOSED_LOST';
    });
    const includedStageCounts = includedStageRows.map((stage) => {
      return stage.nowCount;
    });
    const currentStageCounts = includedStageCounts.filter((count) => {
      return count !== null;
    });

    return Math.max(1, ...currentStageCounts);
  }, [stageRows]);

  const keyMetricCards = React.useMemo(() => {
    const collectClientRnSetForDates = (dates, clientRnSetsByDate) => {
      return dates.reduce((nextClientRnSet, date) => {
        const currentClientRnSet = clientRnSetsByDate[date];

        if (!currentClientRnSet) {
          return nextClientRnSet;
        }

        currentClientRnSet.forEach((clientRn) => {
          nextClientRnSet.add(clientRn);
        });

        return nextClientRnSet;
      }, new Set());
    };
    const buildMetric = (label, metricKey) => {
      const clientRnSetsByDate = setsByKeyAndDate[metricKey] || {};
      const currentSeries = logMetricsState.keyMetricsGraphDates.map((date) => {
        return clientRnSetsByDate[date]?.size || 0;
      });
      const currentClientRnSet = collectClientRnSetForDates(logMetricsState.keyMetricsCurrentPeriodDates, clientRnSetsByDate);
      const previousClientRnSet = collectClientRnSetForDates(logMetricsState.keyMetricsPreviousPeriodDates, clientRnSetsByDate);

      const currentCount = currentClientRnSet.size;
      const previousCount = previousClientRnSet.size;
      const delta = currentCount - previousCount;
      let deltaPercentage = 0;

      if (previousCount === 0 && currentCount > 0) {
        deltaPercentage = 100;
      } else if (previousCount > 0) {
        deltaPercentage = (delta / previousCount) * 100;
      }

      return {
        label,
        currentCount,
        previousCount,
        delta,
        deltaPercentage,
        series: currentSeries,
      };
    };

    return [
      buildMetric(
          translate('metrics.LeadFunnel.KeyMetrics.Leads'),
          'LEADS',
      ),
      buildMetric(
          translate('metrics.LeadFunnel.KeyMetrics.MeetGreet'),
          'MEET_GREET',
      ),
      buildMetric(
          translate('metrics.LeadFunnel.KeyMetrics.Starts'),
          'STARTS',
      ),
    ];
  }, [
    setsByKeyAndDate, logMetricsState.keyMetricsCurrentPeriodDates, logMetricsState.keyMetricsPreviousPeriodDates,
    logMetricsState.keyMetricsGraphDates,
  ]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: '#F9F8F6',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1160px',
          mx: 'auto',
          px: {xs: 2, md: 4},
          py: {xs: 3, md: 5.5},
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{xs: 'column', lg: 'row'}}
            justifyContent='space-between'
            alignItems={{xs: 'flex-start', lg: 'center'}}
            spacing={2}
          >
            <Box sx={{minWidth: 0}}>
              <Typography
                sx={{
                  fontSize: {xs: '24px', md: '28px'},
                  lineHeight: '36px',
                  color: '#1A1916',
                  mb: 1,
                }}
              >
                <Translate expression='metrics.LeadFunnel.Title' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: '17px',
                  color: '#A8A49E',
                }}
              >
                <Translate expression='metrics.LeadFunnel.Subtitle' />
              </Typography>
            </Box>
            <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#919191',
                }}
              >
                <Translate expression='metrics.LeadFunnel.VsPrior' />
              </Typography>
              <ButtonBase
                onClick={(event) => setDatePickerAnchorEl(event.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EBEBEB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <CalendarTodayOutlined sx={{fontSize: 16, color: '#919191'}} />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6B6761',
                  }}
                >
                  {formattedDateRange}
                </Typography>
              </ButtonBase>
              {
                datePickerAnchorEl &&
                <Popover
                  open={Boolean(datePickerAnchorEl)}
                  anchorEl={datePickerAnchorEl}
                  onClose={() => setDatePickerAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: '12px',
                        border: '1px solid #E8E4DE',
                        boxShadow: '0px 12px 32px rgba(26, 25, 22, 0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{p: 1}}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateCalendar
                        value={moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone)}
                        maxDate={latestAvailableDate}
                        onChange={(nextDate) => {
                          if (!nextDate?.isValid?.()) {
                            return;
                          }

                          setSelectedCurrentDate(nextDate.clone().tz(timezone).format('YYYY-MM-DD'));
                          setDatePickerAnchorEl(null);
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Popover>
              }
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  backgroundColor: '#EEEEEE',
                  borderRadius: '8px',
                }}
              >
                <ButtonBase
                  onClick={() => setComparisonMode('DAILY')}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'DAILY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'DAILY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'DAILY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.LeadFunnel.Daily' />
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => setComparisonMode('WEEKLY')}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'WEEKLY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'WEEKLY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'WEEKLY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.LeadFunnel.Weekly' />
                  </Typography>
                </ButtonBase>
              </Box>
            </Stack>
          </Stack>

          <KeyMetricsSection
            isLoading={logMetricsState.isLoading && !logMetricsState.hasLoadedOnce}
            errorMessage={logMetricsState.errorMessage}
            keyMetricCards={keyMetricCards}
            sectionTitleExpression='metrics.LeadFunnel.KeyMetricsSection'
          />

          <FunnelBreakdownSection
            isLoading={stageMetricsState.isLoading && !stageMetricsState.hasLoadedOnce}
            errorMessage={stageMetricsState.errorMessage}
            stageMissingDates={stageMissingDates}
            stageRows={stageRows}
            stageDistributionMax={stageDistributionMax}
          />

          <CohortSection
            chartHeight={340}
            graphSections={cohortGraphSections}
            currentDate={cohortCurrentDate}
            previousDates={cohortPreviousDates}
          />
        </Stack>
      </Box>
    </Box>
  );
};

LeadFunnel.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(LeadFunnel));
